import MultiSelectTagFilter from '@components/filter-component/MultiSelectTagFilter';
import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import Spinner from '@components/spinner/Spinner';
import { LENS_DOCS, TAG_THEME } from '@constants/analysisLenses';
import { Alert, Box, Button, Checkbox, Chip, Link, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { formatInTitleCase } from '@utils/constants';
import { formatDate } from '@utils/dateFormat';
const DEFAULT_DOC = 'https://qualz.ai/docs/userguide/ai-analysis/what-is-multi-lens-analysis/';

const chipSx = {
  fontSize: 10,
  bgcolor: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.background || '#e3f2fd';
  },
  color: (theme) => {
    const tagName = theme.ownerState?.label;
    return TAG_THEME[tagName]?.text || '#7d5ae8';
  },
};

export function TranscriptCard({
  selectedLens,
  txLoading,
  txError,
  assignedTagList,
  setFilteredTagList,
  transcriptsResponse,
  id,
  setSelectedTranscriptIds,
  selectedTranscriptIds,
  setActiveStep,
  handleContinueToEstimate,
  searchQuery,
  isFetching,
  setSearchQuery,
  paginationModel,
  setPaginationModel,
}) {
  // Handle row selection changes
  const handleRowSelectionChange = (newSelectionModel) => {
    // Get current page rows
    const currentPageRows = transcriptsResponse?.transcripts || [];
    const currentPageIds = currentPageRows.map((row) => row.id);

    // Remove current page IDs from existing selection
    const selectionFromOtherPages = selectedTranscriptIds.filter(
      (id) => !currentPageIds.includes(id),
    );

    // Add new selections from current page
    const finalSelection = [...selectionFromOtherPages, ...newSelectionModel];

    setSelectedTranscriptIds(finalSelection);
  };
  // Calculate current page selection for display
  const getCurrentPageSelection = () => {
    const currentPageRows = transcriptsResponse?.transcripts || [];
    const currentPageIds = currentPageRows.map((row) => row.id);

    // Return only the selected IDs that exist on current page
    return selectedTranscriptIds.filter((id) => currentPageIds.includes(id));
  };
  return (
    <>
      <Box sx={{ mb: 4, p: 3, mx: 'auto', border: '1px solid #e8e8e8' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Select Transcripts
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Choose completed transcripts to run the <strong>{selectedLens?.name}</strong> analysis.
          Only transcripts marked as <b>Complete</b> appear here. If a transcript is missing, open
          the Transcripts view and mark it as complete.
        </Typography>

        {txLoading ? (
          <Spinner />
        ) : txError ? (
          <Typography color="error">Failed to load transcripts.</Typography>
        ) : (
          <>
            <Box display={'flex'} justifyContent={'flex-end'}>
              {' '}
              <Box width={'200px'} mr={1}>
                <MultiSelectTagFilter
                  options={assignedTagList}
                  searchQuery={searchQuery}
                  isFetching={isFetching}
                  setSearchQuery={setSearchQuery}
                  label="Filter By Tag"
                  searchControl={true}
                  onFilterChange={(selectedIds) => {
                    setFilteredTagList(selectedIds);
                  }}
                  isFiltering={txLoading}
                />
              </Box>
            </Box>
            <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
              <Box
                sx={{
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2">
                  Selected Lens: {selectedLens?.name} &nbsp;&nbsp; &nbsp;
                </Typography>
                <Typography variant="body2">
                  <Link
                    href={LENS_DOCS[selectedLens?.id]?.userGuide || DEFAULT_DOC}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn More
                  </Link>
                </Typography>
              </Box>
            </Alert>
            <Box
              sx={{
                width: '100%', // Controls the container width
                '& .MuiDataGrid-root': {
                  overflowX: 'auto', // Enables horizontal scrolling
                },
              }}
            >
              <DataGrid
                getRowHeight={() => 'auto'}
                columns={[
                  {
                    field: 'name',
                    headerName: 'Transcript Name',
                    flex: 1,
                    minWidth: 200,

                    renderCell: (params) => (
                      <Link
                        href={`/interview/${id}/transcripts/${params.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {params?.value || params?.id}
                      </Link>
                    ),
                  },
                  {
                    field: 'participant_type',
                    headerName: 'Participant Type',
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => {
                      const participantType = params.value;
                      return (
                        <Chip
                          label={participantType}
                          size="small"
                          sx={{
                            ...chipSx,
                            bgcolor: TAG_THEME[participantType]?.background || '#e3f2fd',
                            color: TAG_THEME[participantType]?.text || '#7d5ae8',
                          }}
                        />
                      );
                    },
                  },
                  {
                    field: 'location',
                    minWidth: 240,
                    headerName: 'Location',
                    flex: 1,
                    valueFormatter: (params) => params.value || '--',
                  },
                  {
                    field: 'created_at',
                    headerName: 'Submission Date',
                    flex: 1,
                    minWidth: 150,
                    renderCell: (params) => {
                      return params.value ? <>{formatDate(params.value)}</> : <>Unknown</>;
                    },
                  },

                  {
                    field: 'assigned_tags',
                    headerName: 'Applied Tags',
                    flex: 1,

                    minWidth: 190,
                    renderCell: (params) => {
                      const tags = params.value;

                      if (!Array.isArray(tags) || tags.length === 0) {
                        return <Typography color="text.secondary">No tags</Typography>;
                      }

                      return (
                        <Box display="flex" flexDirection="column" gap={1} flexWrap={'wrap'}>
                          {tags.map((tagGroup) => (
                            <Box
                              key={tagGroup.tag_key_id}
                              sx={{
                                backgroundColor: '#e3f2fd',
                                borderRadius: 1,
                                width: 'fit-content',
                                px: 1,
                                my: 0.3,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant="caption" fontWeight="bold" py={0.4}>
                                {formatInTitleCase(tagGroup.tag_key)}:
                              </Typography>{' '}
                              <Typography
                                variant="caption"
                                component="span"
                                py={0.4}
                                sx={{ ml: 0.5 }}
                              >
                                {tagGroup.values
                                  .map((val) => formatInTitleCase(val.value_key))
                                  .join(', ')}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    },
                  },
                ]}
                rows={transcriptsResponse?.transcripts || []}
                getRowId={(row) => row.id}
                checkboxSelection
                keepNonExistentRows={true}
                slots={{
                  baseCheckbox: (props) => (
                    <Checkbox checkedIcon={<CheckedIcon />} icon={<UnCheckedIcon />} {...props} />
                  ),
                }}
                hideFooterSelectedRowCount={true}
                // Use the current page selection for display
                rowSelectionModel={getCurrentPageSelection()}
                // Handle selection changes with persistence logic
                onRowSelectionModelChange={handleRowSelectionChange}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                  setPaginationModel({
                    page: model.page,
                    pageSize: model.pageSize,
                  });
                }}
                rowCount={transcriptsResponse?.pagination?.total || 0}
                pageSizeOptions={[5, 10, 25]}
                paginationMode="server"
                disableSelectionOnClick
                disableColumnMenu
                disableColumnSelector
                disableDensitySelector
              />
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',

          mx: 'auto',
          width: '100%',
        }}
      >
        <Button variant="outlined" onClick={() => setActiveStep(0)}>
          Back
        </Button>
        <Button
          variant="contained"
          disabled={selectedTranscriptIds.length === 0}
          onClick={handleContinueToEstimate}
        >
          Continue
        </Button>
      </Box>
    </>
  );
}
